// src/modules/workOrders/services/workOrderService.ts
/**
 * Work Orders module — service layer. Bridges the UI to the repository
 * (never the Firestore SDK directly — ADR 0002). WO numbers are derived
 * server-side from the order's OC serial by the `allocateOrderDocumentNumber`
 * CF (`WWO-2026-0042`, then `…A`, `…B`). Creation is client-side via the
 * repository — there is no Work Order Cloud Function.
 *
 * Required-but-dormant type fields are written as empty defaults at create
 * time (the WorkOrder type marks sourceQuoteId / type / assignedTo / lines /
 * materials / labour as required).
 */
import { repositories } from '../../../data/repositories';
import { allocateOrderDocumentNumber } from '../../../lib/orderDocumentNumber';
import { byCreatedDesc } from '../../../utils/sort';
import { canAdvanceWorkOrder, isOpenWorkOrder } from './workOrderRules';
import type {
  EntityId,
  WorkOrder,
  WorkOrderLineItem,
  WorkOrderStatus,
  WorkOrderType,
} from '../../../types';

const persist = async (wo: WorkOrder): Promise<void> => {
  await repositories.workOrders.save(wo);
};

/** One line of a new WO. `unit` defaults to 'each'. `sourceQuoteLineId` is the
 *  optional soft tag back to the order's quote cart-line id. */
export interface CreateWorkOrderLineInput {
  description: string;
  unit?: string;
  quantity: number;
  sourceQuoteLineId?: EntityId;
}

export interface CreateWorkOrderInput {
  orderId: EntityId;
  /** The order's active quote id — written to the required `sourceQuoteId`. */
  sourceQuoteId: EntityId;
  /** Production type; defaults to FABRICATION. */
  type?: WorkOrderType;
  lines: CreateWorkOrderLineInput[];
  notes?: string;
}

/**
 * Create a DRAFT work order against an order. Validates an order link + at
 * least one valid line (description + quantity > 0); allocates a WO number;
 * writes the dormant required fields as empty defaults.
 */
export const createWorkOrder = async (
  input: CreateWorkOrderInput,
  actor: string,
): Promise<WorkOrder> => {
  if (!input.orderId) throw new Error('A work order must be tied to an order.');
  if (!input.sourceQuoteId) throw new Error('A work order needs the order’s active quote.');

  const cleanedLines: WorkOrderLineItem[] = (input.lines ?? [])
    .filter((l) => l && l.description?.trim() && (l.quantity || 0) > 0)
    .map((l) => ({
      id: `wol_${crypto.randomUUID()}`,
      description: l.description.trim(),
      unit: (l.unit || 'each').trim() || 'each',
      quantity: Math.max(0, l.quantity || 0),
      // Optional soft tag — omit entirely when absent (Firestore rejects undefined).
      ...(l.sourceQuoteLineId ? { sourceQuoteLineId: l.sourceQuoteLineId } : {}),
    }));
  if (cleanedLines.length === 0) {
    throw new Error('A work order needs at least one line item.');
  }

  // `{L}WO-YYYY-NNNN[X]` — derived server-side from the order's OC serial;
  // the second WO on the same order gets `A`, the third `B`.
  const number = await allocateOrderDocumentNumber(input.orderId, 'WO');
  const now = new Date().toISOString();

  const wo: WorkOrder = {
    id: `wo_${crypto.randomUUID()}`,
    woNumber: number,
    orderId: input.orderId,
    sourceQuoteId: input.sourceQuoteId,
    status: 'DRAFT',
    type: input.type ?? 'FABRICATION',
    // Dormant required fields → empty defaults.
    assignedTo: [],
    lines: cleanedLines,
    materials: [],
    labour: [],
    ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
    createdAt: now,
    createdBy: actor,
    updatedAt: now,
    updatedBy: actor,
  };

  await persist(wo);
  return wo;
};

export const fetchWorkOrder = async (id: EntityId): Promise<WorkOrder | null> =>
  repositories.workOrders.findById(id);

export const listWorkOrdersByOrder = async (orderId: EntityId): Promise<WorkOrder[]> => {
  const all = await repositories.workOrders.findByOrder(orderId);
  return all.sort(byCreatedDesc);
};

export const listWorkOrders = async (): Promise<WorkOrder[]> => {
  const all = await repositories.workOrders.list();
  return all.sort(byCreatedDesc);
};

/** Advance a WO along the operated machine (DRAFT → IN_PROGRESS → COMPLETED),
 *  stamping actualStart / actualEnd. Throws on an illegal transition. */
export const advanceWorkOrderStatus = async (
  id: EntityId,
  to: WorkOrderStatus,
  actor: string,
): Promise<WorkOrder> => {
  const current = await fetchWorkOrder(id);
  if (!current) throw new Error(`Work order ${id} not found.`);
  if (!canAdvanceWorkOrder(current.status, to)) {
    throw new Error(`Cannot move a work order from ${current.status} to ${to}.`);
  }
  const now = new Date().toISOString();
  const updated: WorkOrder = { ...current, status: to, updatedAt: now, updatedBy: actor };
  if (to === 'IN_PROGRESS') updated.actualStart = now;
  else if (to === 'COMPLETED') updated.actualEnd = now;
  await persist(updated);
  return updated;
};

/** Cancel a non-terminal WO. The reason is appended to notes (the WorkOrder
 *  type has no cancelledReason field). */
export const cancelWorkOrder = async (
  id: EntityId,
  reason: string,
  actor: string,
): Promise<WorkOrder> => {
  const current = await fetchWorkOrder(id);
  if (!current) throw new Error(`Work order ${id} not found.`);
  if (!canAdvanceWorkOrder(current.status, 'CANCELLED')) {
    throw new Error(`A ${current.status} work order cannot be cancelled.`);
  }
  const now = new Date().toISOString();
  const cancelNote = reason?.trim() ? `Cancelled: ${reason.trim()}` : '';
  const updated: WorkOrder = {
    ...current,
    status: 'CANCELLED',
    updatedAt: now,
    updatedBy: actor,
    ...(cancelNote ? { notes: [current.notes, cancelNote].filter(Boolean).join('\n') } : {}),
  };
  await persist(updated);
  return updated;
};

/** Re-export so UI can filter the cross-order list. */
export { isOpenWorkOrder };
